import { Box, Card, CardActionArea, Typography } from "@mui/material";
import Breadcrumb from "@/components/Breadcrumb";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import * as MuiIcons from "@mui/icons-material";
import { useUserInfo } from "@/context/UserInfoContext";
import Copyright from "@/components/Copyright";
import menu from "@/layouts/menu";

interface QuickAccessCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  permission: string;
  color: string;
}

const cardMeta: Record<string, { description: string; color: string }> = {
  baseprice: {
    description: "จัดการราคาพื้นฐานของ Material",
    color: "primary.main",
  },
  "exchange.rate": {
    description: "จัดการอัตราแลกเปลี่ยนสกุลเงิน",
    color: "primary.main",
  },
  "return.scrap": {
    description: "จัดการราคาและ Supplier สำหรับ Return Scrap",
    color: "primary.main",
  },
  breakdown: {
    description: "จัดการ 1st Tier Supplier และปรับแต่งราคาชิ้นส่วน",
    color: "primary.main",
  },
  flaggedpart: {
    description: "ตรวจสอบชิ้นส่วนที่ติด Flag",
    color: "error.main",
  },
  "material.supplier": {
    description: "จัดการ Material Supplier และปรับแต่ง Material Code",
    color: "primary.main",
  },
  role: {
    description: "จัดการข้อมูลบทบาทและสิทธิ์ของผู้ใช้งาน",
    color: "primary.main",
  },
  log: { description: "ตรวจสอบประวัติการใช้งาน", color: "primary.main" },
  "pa.part": {
    description: "ตรวจสอบข้อมูล PA Part สำหรับ PA RFQ",
    color: "primary.main",
  },
  "pa.supplier": {
    description: "ตรวจสอบข้อมูล PA Supplier สำหรับ PA RFQ",
    color: "primary.main",
  },
  "master.material": {
    description: "จัดการข้อมูลประเภทและสเปคของ Material",
    color: "secondary.main",
  },
  "master.process": {
    description: "จัดการข้อมูล Process ต่างๆ",
    color: "secondary.main",
  },
  "master.system": {
    description: "จัดการข้อมูลหลักอื่นๆ ของระบบ",
    color: "secondary.main",
  },
  "master.buyerboss": {
    description: "จัดการข้อมูล Master Buyer Manager",
    color: "secondary.main",
  },
};

const Home = () => {
  const navigate = useNavigate();
  const { canView } = usePermissions();
  const userInfo = useUserInfo();

  useEffect(() => {
    document.title = "Home | AMPA";
  }, []);

  const quickAccessCards: QuickAccessCard[] = menu
    .filter((item) => item.key in cardMeta)
    .map((item) => {
      const Icon = (MuiIcons as Record<string, React.ElementType>)[item.icon];
      return {
        title: item.name,
        description: cardMeta[item.key].description,
        icon: <Icon sx={{ fontSize: 32 }} />,
        path: item.path,
        permission: item.key,
        color: cardMeta[item.key].color,
      };
    });

  const filteredCards = quickAccessCards.filter((card) =>
    canView(card.permission),
  );

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa" }}>
      <Box sx={{ px: 3, py: 3 }}>
        <Breadcrumb current="home" />
      </Box>
      <Box
        sx={{
          background: "linear-gradient(135deg, #00A8A9 0%, #007879 100%)",
          color: "white",
          py: 4,
          px: 3,
          mb: 5,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "400px",
            height: "400px",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(30%, -30%)",
          },
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              สวัสดี {userInfo?.name?.split(" ")[0]} !
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                opacity: 0.95,
                fontSize: { xs: "1.1rem", md: "1.5rem" },
              }}
            >
              ยินดีต้อนรับเข้าสู่ระบบ Automatic Material Price Adjustment (AMPA)
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              aria-hidden
              src="/homeImage.png"
              alt="Home Image"
              width="300"
              height="auto"
              style={{
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: "1200px", mx: "auto", px: 3, pb: 6, mb: 4 }}>
        <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              bgcolor: "#00A8A9",
              borderRadius: 1,
            }}
          />
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
            เมนูหลัก
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {filteredCards.map((card) => (
            <Card
              key={card.title}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  borderColor: card.color,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleCardClick(card.path)}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        card.color === "primary.main"
                          ? "#00A8A917"
                          : card.color === "secondary.main"
                            ? "#ED6F2D17"
                            : card.color === "error.main"
                              ? "#f4433617"
                              : card.color + "17",
                      color: card.color,
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#333",
                        mb: 0.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        lineHeight: 1.5,
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>
      <Box sx={{ position: "absolute", width: "100%", bottom: 16 }}>
        <Copyright />
      </Box>
    </Box>
  );
};

export default Home;
